import Cookies from "js-cookie";
import jwt_decode from "jwt-decode";

const SESSION_VIEWING_VISIT_ID = "viewingVisitID";
const SESSION_SUMMARY_VIEWS = "summaryViews";
const SESSION_EMR_TOKEN_JWT = "emrTokenJWT";
const SESSION_EMR_FIRST_NAME = "firstName";
const SESSION_EMR_LAST_NAME = "lastName";
const SESSION_EMAIL = "email";
const SESSION_VISIT_INDEX_FILTER = "visitIndexFilter";
const SESSION_PATIENT_REPLY_CHANNEL = "patientReplyChannel";
const SESSION_CARE_PERFORMANCE_RANGE = "carePerformanceRange";
const SESSION_CARE_PERFORMANCE_HEAT = "carePerformanceHeat";
const SESSION_CARE_PERFORMANCE_SORT = "carePerformanceSort";

// expire in 1 day
const SESSION_EXPIRES_DAYS = 1;

export function setViewingVisitID(visitID) {
  // sessionStorage.setItem(SESSION_VIEWING_VISIT_ID, visitID);
  return setCookieWithExpiration(SESSION_VIEWING_VISIT_ID, visitID);
}
export function getViewingVisitID() {
  // return sessionStorage.getItem(SESSION_VIEWING_VISIT_ID);
  return Cookies.get(SESSION_VIEWING_VISIT_ID);
}
export function setSummaryViews(summaryViews) {
  // sessionStorage.setItem(SESSION_SUMMARY_VIEWS, summaryViews);
  return setCookieWithExpiration(SESSION_SUMMARY_VIEWS, summaryViews);
}
export function getSummaryViews() {
  // return sessionStorage.getItem(SESSION_SUMMARY_VIEWS);
  return Cookies.Get(SESSION_SUMMARY_VIEWS);
}
export function getFirstName() {
  // return sessionStorage.getItem(SESSION_EMR_FIRST_NAME);
  return Cookies.get(SESSION_EMR_FIRST_NAME);
}
export function setFirstName(firstName) {
  // sessionStorage.setItem(SESSION_EMR_FIRST_NAME, firstName);
  return setCookieWithExpiration(SESSION_EMR_FIRST_NAME, firstName);
}

export function getLastName() {
  // return sessionStorage.getItem(SESSION_EMR_LAST_NAME);
  return Cookies.get(SESSION_EMR_LAST_NAME);
}
export function setLastName(lastName) {
  // sessionStorage.setItem(SESSION_EMR_LAST_NAME, lastName);
  return setCookieWithExpiration(SESSION_EMR_LAST_NAME, lastName);
}

export function getEmail() {
  // return sessionStorage.getItem(SESSION_EMAIL);
  return Cookies.get(SESSION_EMAIL);
}
export function setEmail(email) {
  // sessionStorage.setItem(SESSION_EMAIL, email);
  return setCookieWithExpiration(SESSION_EMAIL, email);
}
export function isAssistant() {
  const decoded = jwt_decode(getEMRTokenJWT());
  return decoded.isAssistant || false;
}
export function isAdmin() {
  const decoded = jwt_decode(getEMRTokenJWT());
  return decoded.isAdmin || false;
}
export function isSuperAdmin() {
  const decoded = jwt_decode(getEMRTokenJWT());
  return decoded.isSuperAdmin || false;
}
export function isMedicalDirector() {
  const decoded = jwt_decode(getEMRTokenJWT());
  return decoded.isMedicalDirector || false;
}
// Reporting-only role: opens the care-message performance screen and nothing
// else. Like every isX() here it reads a decoded JWT in the browser, so it is a
// convenience for hiding UI — the GraphQL resolver is what actually enforces it.
export function isAssistantsSupervisor() {
  const decoded = jwt_decode(getEMRTokenJWT());
  return decoded.isAssistantsSupervisor || false;
}
export function isCSAgent() {
  const decoded = jwt_decode(getEMRTokenJWT());
  return decoded.isCSAgent || false;
}
export function isScriptsureUser() {
  const decoded = jwt_decode(getEMRTokenJWT());
  return decoded.scriptsureEmail != ""; // has a scriptsure email, therefore is a scriptsure user
}
export function getEMRTokenJWT() {
  return Cookies.get(SESSION_EMR_TOKEN_JWT);
}
export function setEMRTokenJWT(token) {
  return setCookieWithExpiration(SESSION_EMR_TOKEN_JWT, token);
}
export function getVisitIndexFilter() {
  return Cookies.get(SESSION_VISIT_INDEX_FILTER);
}
export function setVisitIndexFilter(filter) {
  return setCookieWithExpiration(SESSION_VISIT_INDEX_FILTER, filter);
}
// Channel sub-filter for the Patient Replied list ("" = All, "care", "doctor").
// Stored like visitIndexFilter so opening a visit and coming back returns you to
// the list you were working, instead of resetting to All every time.
//
// Deliberately a SESSION cookie (no expiry — see setCookieWithExpiration): the
// narrowing should survive navigation and a reload, but not a new browser
// session. A narrowing that outlives the day is how the other channel goes
// unwatched for a week.
export function getPatientReplyChannelFilter() {
  return Cookies.get(SESSION_PATIENT_REPLY_CHANNEL) || "";
}
export function setPatientReplyChannelFilter(channel) {
  return setCookieWithExpiration(SESSION_PATIENT_REPLY_CHANNEL, channel || "");
}

// Selected range on the care-message performance screen — a preset key ("7",
// "today", …) or "from|to" for a custom range. Stored so a refresh, or coming
// back from a visit, returns you to the window you were reading rather than
// resetting to the default and quietly showing different numbers.
//
// A SESSION cookie (no expiry, like the other EMR filters): the selection should
// survive navigation and reloads, but a new browser session starting on the
// default is the right amount of memory for a reporting screen.
export function getCarePerformanceRange() {
  return Cookies.get(SESSION_CARE_PERFORMANCE_RANGE) || "";
}
export function setCarePerformanceRange(value) {
  return setCookieWithExpiration(SESSION_CARE_PERFORMANCE_RANGE, value || "");
}

// Cell colouring on the care-message performance screen: "heatmap" (red-green),
// "shaded" (one hue, light to dark), or "none". Stored because it is an
// accessibility preference as much as a taste one — someone who switches to
// "shaded" because they cannot read the red-green ramp should not have to switch
// again on every page load. Session cookie, like the other EMR view settings.
export function getCarePerformanceHeatMode() {
  return Cookies.get(SESSION_CARE_PERFORMANCE_HEAT) || "";
}
export function setCarePerformanceHeatMode(mode) {
  return setCookieWithExpiration(SESSION_CARE_PERFORMANCE_HEAT, mode || "");
}
// Sort order for a table on the care-message performance screen, stored per
// panel ("grid", "volume", "speed") as "<key>:<asc|desc>". Persisted for the
// same reason the range is — someone comparing one assistant day over day
// should not have to re-find them in a differently ordered table on every load.
export function getCarePerformanceSort(panel) {
  return Cookies.get(`${SESSION_CARE_PERFORMANCE_SORT}_${panel}`) || "";
}
export function setCarePerformanceSort(panel, value) {
  return setCookieWithExpiration(
    `${SESSION_CARE_PERFORMANCE_SORT}_${panel}`,
    value || "",
  );
}
export function getDoctorID() {
  const decoded = jwt_decode(getEMRTokenJWT());
  return decoded.doctorID;
}
export function getEMRUserID() {
  const decoded = jwt_decode(getEMRTokenJWT());
  return decoded.userID;
}

export function isMedicalProvider() {
  const decoded = jwt_decode(getEMRTokenJWT());
  const emptyUUID = "00000000-0000-0000-0000-000000000000";
  return decoded.doctorID && decoded.doctorID !== "" && decoded.doctorID !== emptyUUID;
}

export function clearCookies() {
  Cookies.remove(SESSION_VIEWING_VISIT_ID);
  Cookies.remove(SESSION_SUMMARY_VIEWS);
  Cookies.remove(SESSION_EMR_TOKEN_JWT);
  Cookies.remove(SESSION_EMR_FIRST_NAME);
  Cookies.remove(SESSION_EMR_LAST_NAME);
  Cookies.remove(SESSION_EMAIL);
  Cookies.remove(SESSION_VISIT_INDEX_FILTER);

  setEMRTokenJWT("");
}

export function isLoggedIn() {
  // Weird caching bug? where using Cookies.get(SESSION_EMR_TOKEN_JWT ) incorrectly returns a cleared value
  // console.log("Cookies:", Cookies.get());
  const token = Cookies.get()[SESSION_EMR_TOKEN_JWT];
  if (token != null) {
    try {
      const decoded = jwt_decode(token);
      const expDate = new Date(decoded.exp * 1000); // js uses nanoseconds, so need to x1000 to compare
      // console.log("expDate:", expDate);
      // console.log(expDate > new Date().getTime());
      return expDate > new Date().getTime(); // logged in if expiration date is > now.
    } catch (error) {
      return false;
    }
  }

  // return token != null && token != "";
  return false;
}

function setCookieWithExpiration(key, value, expires = null) {
  if (expires == null) {
    // No "expires" sets session-only cookie
    return Cookies.set(key, value);
  } else {
    // persistent cookie with expiration
    return Cookies.set(key, value, { expires: expires });
  }
}
